import { create } from 'zustand';
import { API_URL } from '@/config/api';

/**
 * Zustand store for caching workspace and hub data
 * - Caches hub lists per workspace for instant switching
 * - Caches hub content (channels, boards, groups) per hub
 * - Enables prefetching on hover
 */
export const useWorkspaceHubsCache = create((set, get) => ({
  // Workspace data
  workspaces: [],
  currentWorkspace: null,
  isLoadingWorkspaces: false,
  
  // Hubs per workspace cache: { workspaceId: Hub[] }
  hubsByWorkspace: {},
  isLoadingHubs: false,
  
  // Hub content cache: { hubId: { channels, boards, groups, canvas, lastFetched } }
  hubContentCache: {},
  
  // DM conversations (not hub-scoped)
  dmConversations: [],
  
  // Prefetch state
  prefetchingHubId: null,
  
  // Actions
  
  // Fetch all workspaces
  fetchWorkspaces: async (token) => {
    if (!token || get().isLoadingWorkspaces) return;
    
    set({ isLoadingWorkspaces: true });
    try {
      const response = await fetch(`${API_URL}/workspaces`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        set({ workspaces: data });
        
        // Auto-select workspace if none selected
        const current = get().currentWorkspace;
        if (!current && data.length > 0) {
          const savedId = localStorage.getItem('selectedWorkspaceId');
          const saved = data.find(w => w.id === savedId);
          set({ currentWorkspace: saved || data[0] });
        }
        return data;
      }
    } catch (error) {
      console.error('Error fetching workspaces:', error);
    } finally {
      set({ isLoadingWorkspaces: false });
    }
    return [];
  },
  
  // Set current workspace
  setCurrentWorkspace: (workspace) => {
    set({ currentWorkspace: workspace });
    if (workspace) {
      localStorage.setItem('selectedWorkspaceId', workspace.id);
    }
  },
  
  // Fetch hubs for a workspace (always fetch fresh, update cache)
  fetchHubs: async (token, workspaceId, forceRefresh = false) => {
    if (!token || !workspaceId) return [];
    
    // Return cached data for instant UI, but still fetch fresh data
    const cached = get().hubsByWorkspace[workspaceId];
    
    // If not forcing refresh and we have cache, return it but fetch in background
    if (!forceRefresh && cached && cached.length > 0) {
      // Fetch fresh data in background to update cache
      get()._fetchHubsFromApi(token, workspaceId);
      return cached;
    }
    
    // No cache or force refresh - fetch and wait
    return await get()._fetchHubsFromApi(token, workspaceId);
  },
  
  // Internal function to fetch hubs from API
  _fetchHubsFromApi: async (token, workspaceId) => {
    set({ isLoadingHubs: true });
    try {
      const response = await fetch(
        `${API_URL}/hubs?workspace_id=${workspaceId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const hubs = await response.json();
        set((state) => ({
          hubsByWorkspace: {
            ...state.hubsByWorkspace,
            [workspaceId]: hubs
          }
        }));
        return hubs;
      }
    } catch (error) {
      console.error('Error fetching hubs:', error);
    } finally {
      set({ isLoadingHubs: false });
    }
    return [];
  },
  
  // Update hubs in cache
  updateHubsCache: (workspaceId, hubs) => {
    set((state) => ({
      hubsByWorkspace: {
        ...state.hubsByWorkspace,
        [workspaceId]: hubs
      }
    }));
  },
  
  // Add a single hub to cache
  addHubToCache: (workspaceId, hub) => {
    set((state) => {
      const existing = state.hubsByWorkspace[workspaceId] || [];
      return {
        hubsByWorkspace: {
          ...state.hubsByWorkspace,
          [workspaceId]: [...existing, hub]
        }
      };
    });
  },
  
  // Remove hub from cache
  removeHubFromCache: (workspaceId, hubId) => {
    set((state) => {
      const existing = state.hubsByWorkspace[workspaceId] || [];
      return {
        hubsByWorkspace: {
          ...state.hubsByWorkspace,
          [workspaceId]: existing.filter(h => h.id !== hubId)
        }
      };
    });
  },
  
  // Fetch hub content (channels, boards, groups)
  fetchHubContent: async (token, hubId) => {
    if (!token || !hubId) return null;
    
    // Check cache (5 minute TTL)
    const cached = get().hubContentCache[hubId];
    const now = Date.now();
    if (cached && (now - cached.lastFetched) < 5 * 60 * 1000) {
      return cached;
    }
    
    try {
      const [channelsRes, boardsRes, groupsRes] = await Promise.all([
        fetch(`${API_URL}/channels?hub_id=${hubId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/whiteboards?hub_id=${hubId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/groups?hub_id=${hubId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      const content = {
        channels: channelsRes.ok ? await channelsRes.json() : [],
        boards: boardsRes.ok ? (await boardsRes.json()).slice(0, 8) : [],
        groups: groupsRes.ok ? await groupsRes.json() : [],
        lastFetched: now
      };
      
      set((state) => ({
        hubContentCache: {
          ...state.hubContentCache,
          [hubId]: content
        }
      }));
      
      return content;
    } catch (error) {
      console.error('Error fetching hub content:', error);
    }
    return null;
  },
  
  // Prefetch hub content on hover
  prefetchHubContent: async (token, hubId) => {
    if (!token || !hubId) return;
    
    // Don't prefetch if already cached or currently prefetching
    const cached = get().hubContentCache[hubId];
    if (cached || get().prefetchingHubId === hubId) return;
    
    set({ prefetchingHubId: hubId });
    await get().fetchHubContent(token, hubId);
    set({ prefetchingHubId: null });
  },
  
  // Invalidate hub content cache
  invalidateHubContent: (hubId) => {
    set((state) => {
      const newCache = { ...state.hubContentCache };
      delete newCache[hubId];
      return { hubContentCache: newCache };
    });
  },
  
  // Fetch DM conversations
  fetchDMConversations: async (token) => {
    if (!token) return [];
    
    try {
      const response = await fetch(`${API_URL}/dm/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        set({ dmConversations: data });
        return data;
      }
    } catch (error) {
      console.error('Error fetching DM conversations:', error);
    }
    return [];
  },
  
  // Invited hubs (hubs user is member of but doesn't own)
  invitedHubs: [],
  hubAssignments: {},  // { hubId: workspaceId }
  
  // Fetch invited hubs
  fetchInvitedHubs: async (token) => {
    if (!token) return [];
    
    try {
      const response = await fetch(`${API_URL}/hubs/invited`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        
        // Build assignment map
        const assignments = {};
        data.forEach(hub => {
          if (hub.assigned_workspace_id) {
            assignments[hub.id] = hub.assigned_workspace_id;
          }
        });
        
        set({ invitedHubs: data, hubAssignments: assignments });
        return data;
      }
    } catch (error) {
      console.error('Error fetching invited hubs:', error);
    }
    return [];
  },
  
  // Assign hub to workspace
  assignHubToWorkspace: async (token, hubId, workspaceId) => {
    if (!token) return false;
    
    try {
      const response = await fetch(`${API_URL}/hubs/${hubId}/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ workspace_id: workspaceId })
      });
      
      if (response.ok) {
        set((state) => ({
          hubAssignments: {
            ...state.hubAssignments,
            [hubId]: workspaceId
          }
        }));
        return true;
      }
    } catch (error) {
      console.error('Error assigning hub:', error);
    }
    return false;
  },
  
  // Remove hub assignment
  removeHubAssignment: async (token, hubId) => {
    if (!token) return false;
    
    try {
      const response = await fetch(`${API_URL}/hubs/${hubId}/assign`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        set((state) => {
          const newAssignments = { ...state.hubAssignments };
          delete newAssignments[hubId];
          return { hubAssignments: newAssignments };
        });
        return true;
      }
    } catch (error) {
      console.error('Error removing hub assignment:', error);
    }
    return false;
  },
  
  // Get hubs for a workspace (owned + assigned invited hubs)
  getWorkspaceHubs: (workspaceId) => {
    const { hubsByWorkspace, invitedHubs, hubAssignments } = get();
    
    // Get owned hubs for this workspace
    const ownedHubs = hubsByWorkspace[workspaceId] || [];
    
    // Get invited hubs assigned to this workspace
    const assignedHubs = invitedHubs.filter(
      hub => hubAssignments[hub.id] === workspaceId
    );
    
    return [...ownedHubs, ...assignedHubs];
  },
  
  // Get unassigned invited hubs
  getUnassignedHubs: () => {
    const { invitedHubs, hubAssignments } = get();
    return invitedHubs.filter(hub => !hubAssignments[hub.id]);
  },
  
  // Clear all cache
  clearCache: () => {
    set({
      workspaces: [],
      currentWorkspace: null,
      hubsByWorkspace: {},
      hubContentCache: {},
      dmConversations: [],
      invitedHubs: [],
      hubAssignments: {}
    });
  },
  
  // Get current workspace's hubs from cache
  getCurrentWorkspaceHubs: () => {
    const { currentWorkspace, hubsByWorkspace } = get();
    if (!currentWorkspace) return [];
    return hubsByWorkspace[currentWorkspace.id] || [];
  }
}));

export default useWorkspaceHubsCache;
